import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown, TrendingUp, Wallet, ShieldCheck, ExternalLink, Trash2,
  Pencil, CreditCard, Loader2, Receipt, MailWarning, X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { BidModal } from "@/components/BidModal";
import { CardSetup } from "@/components/CardSetup";
import { ImageUpload } from "@/components/ImageUpload";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-2 text-white/40 text-xs font-bold uppercase tracking-widest mb-3">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className={`font-display font-black text-3xl tracking-tighter ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [bids, setBids] = useState([]);
  const [totalLocked, setTotalLocked] = useState(0);
  const [showVerifyBanner, setShowVerifyBanner] = useState(true);
  const [editBid, setEditBid] = useState(null);
  const [editPrefillCap, setEditPrefillCap] = useState(null);
  const [tab, setTab] = useState(params.get("tab") === "brandlock" ? "brandlock" : "bids");

  const [blBrand, setBlBrand] = useState("");
  const [blKeyword, setBlKeyword] = useState(params.get("keyword") || "");
  const [blWebsite, setBlWebsite] = useState("");
  const [blEmail, setBlEmail] = useState("");
  const [blApps, setBlApps] = useState([]);

  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => { document.title = "My Bids — PodiumOne"; }, []);

  const loadAll = useCallback(async () => {
    try {
      const [b, a] = await Promise.all([
        api.get("/bids/mine"),
        api.get("/brand-lock/mine"),
      ]);
      setBids(b.data.bids);
      setTotalLocked(b.data.total_locked);
      setBlApps(a.data.applications);
    } catch {}
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    setBlEmail(user.email);
    setProfileName(user.name || "");
    setProfileEmail(user.email || "");
    loadAll();
  }, [user, authLoading, navigate, loadAll]);

  const cancelBid = async (slug, region = "global") => {
    try {
      await api.delete(`/bids/${slug}`, { params: { region } });
      toast.success("Bid cancelled");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveAvatar = async (url) => {
    try {
      await api.post("/auth/profile", { picture: url });
      await refreshUser();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveProfile = async () => {
    const name = profileName.trim();
    const email = profileEmail.trim().toLowerCase();
    if (!name) { toast.error("Display name cannot be empty"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email address"); return; }
    setSavingProfile(true);
    try {
      await api.post("/auth/profile", { name, email });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password changed successfully");
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPw(false);
    }
  };

  const submitBrandLock = async (e) => {
    e.preventDefault();
    try {
      await api.post("/brand-lock", {
        brand_name: blBrand, keyword: blKeyword, website: blWebsite, contact_email: blEmail,
      });
      toast.success("Brand Lock application submitted for review");
      setBlBrand(""); setBlWebsite("");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (authLoading || !user) {
    return <div><Navbar /><div className="flex justify-center pt-40"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div></div>;
  }

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 pt-28 pb-24">
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-8">
          Your dashboard
        </motion.h1>

        {user.is_verified === false && showVerifyBanner && (
          <div data-testid="verify-banner" className="flex items-start gap-3 rounded-2xl border border-yellow-500/40 bg-yellow-500/10 p-4 mb-8">
            <MailWarning className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-yellow-100">
              <span className="font-semibold">Verify your email to start bidding.</span> Check your inbox for the confirmation link to activate your account and claim your $10.00 + RM10.00 credits.
            </div>
            <button data-testid="verify-banner-dismiss" onClick={() => setShowVerifyBanner(false)} className="text-yellow-200/60 hover:text-yellow-100 transition-colors shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
          <StatCard icon={Crown} label="Active bids" value={bids.length} />
          <StatCard icon={TrendingUp} label="Total locked" value={`$${totalLocked.toFixed(2)}`} accent="text-emerald-400" />
        </div>

        <div className="glass rounded-2xl p-6 mb-6" data-testid="profile-card">
          <div className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Profile settings</div>
          <ImageUpload value={user.picture || ""} onChange={saveAvatar} seedName={user.name || user.email} label="" testId="profile-avatar-upload" />
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div>
              <Label className="text-white/70 text-sm">Display name</Label>
              <Input
                data-testid="profile-name-input"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className="mt-1.5 bg-white/5 border-white/15"
              />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Email address</Label>
              <Input
                data-testid="profile-email-input"
                type="email"
                value={profileEmail}
                onChange={(e) => setProfileEmail(e.target.value)}
                className="mt-1.5 bg-white/5 border-white/15"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              data-testid="save-profile-btn"
              onClick={saveProfile}
              disabled={savingProfile || (profileName.trim() === (user.name || "") && profileEmail.trim().toLowerCase() === (user.email || ""))}
              className="rounded-full bg-white text-black font-semibold active:scale-95 transition-all disabled:opacity-40"
            >
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10" data-testid="security-section">
            <div className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Security · Change password</div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-white/70 text-sm">Current password</Label>
                <Input data-testid="old-password-input" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="mt-1.5 bg-white/5 border-white/15" />
              </div>
              <div>
                <Label className="text-white/70 text-sm">New password</Label>
                <Input data-testid="new-password-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5 bg-white/5 border-white/15" />
              </div>
              <div>
                <Label className="text-white/70 text-sm">Confirm new</Label>
                <Input data-testid="confirm-password-input" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="mt-1.5 bg-white/5 border-white/15" />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                data-testid="change-password-btn"
                onClick={changePassword}
                disabled={savingPw || !oldPw || !newPw || !confirmPw}
                variant="outline"
                className="rounded-full border-white/15 hover:bg-white/10 disabled:opacity-40"
              >
                {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="glass border-white/10 mb-6">
            <TabsTrigger value="bids" data-testid="tab-bids">Active bids</TabsTrigger>
            <TabsTrigger value="brandlock" data-testid="tab-brandlock">Brand Lock</TabsTrigger>
          </TabsList>

          <TabsContent value="bids" className="space-y-3">
            {bids.length === 0 ? (
              <div className="glass rounded-2xl p-10 text-center text-white/40">No active bids. Search a keyword to start.</div>
            ) : bids.map((b) => (
              <div key={b.keyword_slug} data-testid={`my-bid-${b.keyword_slug}`} className="glass rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/k/${b.keyword_slug}`)} className="font-display font-bold text-lg tracking-tight capitalize hover:text-blue-400 transition-colors truncate">{b.keyword_name}</button>
                    {b.is_number_one && <span className="inline-flex items-center gap-1 text-xs text-emerald-400 shrink-0"><Crown className="w-3.5 h-3.5" /> #1</span>}
                  </div>
                  <div className="text-sm text-white/50">
                    Rank #{b.rank} · Locked Bid: {b.currency === "MYR" ? "RM " : "$"}{Number(b.locked_bid_amount).toFixed(2)}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {b.rank !== 1 && (
                    <Button
                      data-testid={`outbid-${b.keyword_slug}`}
                      size="sm"
                      onClick={() => setEditBid(b)}
                      className="rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-semibold active:scale-95 transition-all"
                    >
                      <TrendingUp className="w-3.5 h-3.5 mr-1" /> Outbid Now
                    </Button>
                  )}
                  <Button data-testid={`edit-bid-${b.keyword_slug}`} variant="outline" size="sm" onClick={() => setEditBid(b)} className="rounded-full border-white/15 hover:bg-white/10">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button data-testid={`cancel-bid-${b.keyword_slug}`} variant="outline" size="sm" onClick={() => cancelBid(b.keyword_slug, b.region)} className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="brandlock" className="space-y-6">
            <form onSubmit={submitBrandLock} className="glass rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2 text-yellow-400 mb-1"><ShieldCheck className="w-5 h-5" /><span className="font-display font-bold text-lg">Apply for Brand Lock</span></div>
              <p className="text-sm text-white/50">Permanently hold #1 on your own brand keyword. Reviewed manually by our team.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-white/70 text-sm">Brand name</Label><Input data-testid="bl-brand" value={blBrand} onChange={(e) => setBlBrand(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15" /></div>
                <div><Label className="text-white/70 text-sm">Keyword</Label><Input data-testid="bl-keyword" value={blKeyword} onChange={(e) => setBlKeyword(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15" /></div>
                <div><Label className="text-white/70 text-sm">Website</Label><Input data-testid="bl-website" value={blWebsite} onChange={(e) => setBlWebsite(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15" /></div>
                <div><Label className="text-white/70 text-sm">Contact email</Label><Input data-testid="bl-email" type="email" value={blEmail} onChange={(e) => setBlEmail(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15" /></div>
              </div>
              <Button data-testid="bl-submit" type="submit" className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold active:scale-95 transition-all">Submit application</Button>
            </form>

            {blApps.length > 0 && (
              <div className="glass rounded-2xl p-6">
                <div className="text-sm font-bold uppercase tracking-widest text-white/40 mb-4">Your applications</div>
                <div className="space-y-2">
                  {blApps.map((a) => (
                    <div key={a.application_id} data-testid={`bl-app-${a.application_id}`} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div><div className="font-medium">{a.brand_name}</div><div className="text-xs text-white/40 capitalize">{a.keyword}</div></div>
                      <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${a.status === "approved" ? "bg-emerald-500/15 text-emerald-400" : a.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-yellow-500/15 text-yellow-400"}`}>{a.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {editBid && (
        <BidModal
          open={!!editBid}
          onOpenChange={(o) => { if (!o) { setEditBid(null); } }}
          slug={editBid.keyword_slug}
          keywordName={editBid.keyword_name}
          topBid={editBid.rank_1_locked_bid}
          existingBid={editBid}
          symbol={editBid.currency === "MYR" ? "RM " : "$"}
          fee={editBid.currency === "MYR" ? 5 : 10}
          onSuccess={loadAll}
        />
      )}
    </div>
  );
}
